/**
 * Service-area restriction by state. The tenant lists every state its country has
 * (`settings.country.states`), so limiting where the business works is our job: the plugin's
 * "Service states" setting names the allowed codes and the form both restricts the state field
 * and sanity-checks the ZIP against it.
 */

export interface StateZipRule {
  /** ZIPs (5 digits) belonging to the state. */
  zip: RegExp;
  /** ZIPs inside that pattern that actually belong elsewhere. */
  deny?: readonly string[];
}

/**
 * Deliberately generous: a false rejection turns a real customer away, while a false accept
 * only reaches the tenant's own service-area check. Oklahoma owns the 730–749 prefixes outright
 * (73001–74966, every OK ZIP starts 73 or 74); 73301 and 73344 sit in that span but are the
 * IRS's Austin, TX addresses. Add a state here to make it selectable in the setting.
 */
export const STATE_ZIP_RULES: Readonly<Record<string, StateZipRule>> = {
  OK: { zip: /^7[34]\d{3}$/, deny: ['73301', '73344'] },
};

/** Uppercase, de-duplicated two-letter codes; anything else is dropped. */
export function normalizeStateCodes(codes: readonly string[] | null | undefined): string[] {
  if (!codes) return [];
  const seen = new Set<string>();
  for (const raw of codes) {
    const code = String(raw ?? '')
      .trim()
      .toUpperCase();
    if (/^[A-Z]{2}$/.test(code)) seen.add(code);
  }
  return [...seen];
}

/**
 * `unknown` when no allowed state has a ZIP rule — we must never block an address we cannot
 * judge, so callers treat it as a pass.
 */
export function zipMatchesStates(
  zip: string,
  states: readonly string[],
): 'match' | 'mismatch' | 'unknown' {
  const trimmed = zip.trim();
  const known = states.filter((s) => STATE_ZIP_RULES[s]);
  if (!trimmed || known.length === 0) return 'unknown';
  for (const state of known) {
    const rule = STATE_ZIP_RULES[state]!;
    if (rule.zip.test(trimmed) && !(rule.deny ?? []).includes(trimmed)) return 'match';
  }
  return 'mismatch';
}

/** Tenant-provided state names for the allowed codes, falling back to the codes themselves. */
export function stateNames(
  codes: readonly string[],
  names: Record<string, unknown> | null | undefined,
): string[] {
  return codes.map((code) => {
    const name = names?.[code];
    return typeof name === 'string' && name.trim() ? name.trim() : code;
  });
}

/** e.g. "Oklahoma" / "Oklahoma or Texas" / "Oklahoma, Texas or Kansas" */
export function joinStateNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/** Customer-facing, and never names the underlying service. */
export function outsideServiceStatesMessage(names: readonly string[]): string {
  const joined = joinStateNames(names);
  if (!joined) return 'That address is outside our service area.';
  // Phrased to sidestep a/an: any state name reads correctly after "in".
  return names.length === 1
    ? `We currently serve ${joined} only — please enter a ZIP code in ${joined}.`
    : `We currently serve ${joined} only — please enter a ZIP code in one of those states.`;
}
